import { calculateBearing } from "../lab-v14/core/calculateBearing";
import { calculateDistance } from "../lab-v14/core/calculateDistance";
import { calculateRelativeAngle } from "../lab-v14/core/calculateRelativeAngle";
import type { GeoArAccuracyWarning, GeoArStatus } from "../lab-v14/types/GeoArState";
import type { GeoArResult } from "../lab-v14/types/GeoArResult";
import {
  getMapLabelIndexSummary,
  loadMapLabelTuples,
  scheduleMapLabelIndexForPack,
} from "../../../../lib/android-map-label-index";
import { mergeMapLabelTuples } from "../../../../lib/map-label-index-core";
import { mapPackForPoint } from "../../../../../shared/ecuador-map-packs";
import { geoFeatureTypeFromGazetteerKind, type PointIdentifyGazetteerTuple } from "./pointIdentifyCatalog";
import { registerElevationTargetV4 } from "./elevationResolverV4";

export type WideRangeQuery = {
  latitude: number;
  longitude: number;
  accuracy: number;
  heading: number;
  headingAccuracy: number | null;
  horizontalFov: number;
  offline: boolean;
  maxDistanceMeters: number;
  allowedPackIds?: readonly string[];
  maxResults?: number;
};

export type WideRangeScan = {
  results: GeoArResult[];
  scanned: number;
  source: string;
  mapCatalogReady: boolean;
  mapCatalogBuilding: boolean;
};

type GazetteerPayload = {
  source?: string;
  attribution?: string;
  places?: PointIdentifyGazetteerTuple[];
};

const LOCAL_GAZETTEER_URL = "/search/ecuador-gazetteer.json";
const DEFAULT_PACK_IDS = ["sierra-sur", "azuay", "canar", "ecuador"] as const;
let payloadPromise: Promise<GazetteerPayload> | null = null;

function localAssetUrl() {
  if (typeof document !== "undefined" && document.baseURI) {
    return new URL(LOCAL_GAZETTEER_URL.replace(/^\//, ""), document.baseURI).toString();
  }
  return LOCAL_GAZETTEER_URL;
}

async function loadPayload() {
  if (!payloadPromise) {
    payloadPromise = fetch(localAssetUrl(), { cache: "force-cache", headers: { Accept: "application/json" } })
      .then(async (response) => {
        if (!response.ok) throw new Error("POINT_IDENTIFY_WIDE_RANGE_GAZETTEER_UNAVAILABLE");
        const payload = await response.json() as GazetteerPayload;
        if (!Array.isArray(payload.places)) throw new Error("POINT_IDENTIFY_WIDE_RANGE_GAZETTEER_INVALID");
        return payload;
      })
      .catch((error) => {
        payloadPromise = null;
        throw error;
      });
  }
  return payloadPromise;
}

function packAllowed(packIds: readonly string[], allowed: ReadonlySet<string>) {
  return packIds.some((packId) => allowed.has(packId));
}

function accuracyWarnings(accuracy: number, headingAccuracy: number | null): GeoArAccuracyWarning[] {
  const warnings: GeoArAccuracyWarning[] = [];
  if (accuracy > 25) warnings.push("LOW_GPS_ACCURACY");
  if (headingAccuracy === null || headingAccuracy > 20) warnings.push("LOW_HEADING_ACCURACY");
  return warnings;
}

async function loadRegionalMapCatalog(packIds: readonly string[]) {
  const regionalPackIds = [...new Set(packIds.filter((packId) => packId && packId !== "ecuador"))];
  if (!regionalPackIds.length || typeof indexedDB === "undefined") {
    return { tuples: [] as PointIdentifyGazetteerTuple[], ready: false, building: false };
  }
  await Promise.all(regionalPackIds.map((packId, index) =>
    scheduleMapLabelIndexForPack(packId, index === 0).catch(() => null),
  ));
  const packSet = new Set(regionalPackIds);
  const [tuples, summary] = await Promise.all([
    loadMapLabelTuples(packSet).catch(() => []),
    getMapLabelIndexSummary(packSet).catch(() => ({ building: false, readyCount: 0 })),
  ]);
  return {
    tuples: tuples as PointIdentifyGazetteerTuple[],
    ready: Number(summary.readyCount ?? 0) > 0,
    building: Boolean(summary.building),
  };
}

export async function scanPointIdentifyWideRange(query: WideRangeQuery): Promise<WideRangeScan> {
  const maxDistance = Math.max(50_001, Math.min(100_000, query.maxDistanceMeters));
  if (maxDistance <= 50_000) return { results: [], scanned: 0, source: "THERA Range Catalog", mapCatalogReady: false, mapCatalogBuilding: false };

  const payload = await loadPayload();
  const allowedPackIds = query.allowedPackIds ?? DEFAULT_PACK_IDS;
  const preferredPack = mapPackForPoint(query.longitude, query.latitude);
  const orderedPackIds = preferredPack?.id && allowedPackIds.includes(preferredPack.id)
    ? [preferredPack.id, ...allowedPackIds.filter((packId) => packId !== preferredPack.id)]
    : [...allowedPackIds];
  const mapCatalog = await loadRegionalMapCatalog(orderedPackIds);
  const tuples = mergeMapLabelTuples(payload.places ?? [], mapCatalog.tuples) as PointIdentifyGazetteerTuple[];
  const allowed = new Set(allowedPackIds);
  const aimDegrees = Math.max(6, Math.min(12, query.horizontalFov * 0.14));
  const broadAim = Math.min(query.horizontalFov / 2, aimDegrees * 1.35);
  const latDelta = maxDistance / 110_900;
  const lonScale = Math.max(0.25, Math.cos((query.latitude * Math.PI) / 180));
  const lonDelta = maxDistance / (111_320 * lonScale);
  const warnings = accuracyWarnings(query.accuracy, query.headingAccuracy);
  const candidates: Array<{ result: GeoArResult; score: number }> = [];

  for (const tuple of tuples) {
    if (!Array.isArray(tuple) || tuple.length < 8) continue;
    const [id, name, , latitude, longitude, kind, rank, packIds, elevationM, elevationSource, elevationStatus] = tuple;
    if (!id || !name || !Number.isFinite(latitude) || !Number.isFinite(longitude)) continue;
    const normalizedPackIds = Array.isArray(packIds) ? packIds : [];
    if (!packAllowed(normalizedPackIds, allowed)) continue;
    const featureType = geoFeatureTypeFromGazetteerKind(String(kind ?? ""));
    if (!["MOUNTAIN", "VOLCANO", "SUMMIT", "HILL"].includes(String(featureType))) continue;
    registerElevationTargetV4({
      featureUuid: `range-${String(id)}`,
      latitude,
      longitude,
      packIds: normalizedPackIds,
    });
    const rawElevation: unknown = elevationM;
    const altitude = rawElevation === null || rawElevation === undefined || rawElevation === "" ? Number.NaN : Number(rawElevation);
    const validAltitude = Number.isFinite(altitude) && altitude >= -500 && altitude <= 9_000 ? altitude : null;
    if (Math.abs(latitude - query.latitude) > latDelta || Math.abs(longitude - query.longitude) > lonDelta) continue;

    const distance = calculateDistance(query.latitude, query.longitude, latitude, longitude);
    if (distance <= 50_000 || distance > maxDistance) continue;
    const bearing = calculateBearing(query.latitude, query.longitude, latitude, longitude);
    const relativeAngle = calculateRelativeAngle(bearing, query.heading);
    if (Math.abs(relativeAngle) > broadAim) continue;

    const alignment = Math.max(0, 1 - Math.abs(relativeAngle) / aimDegrees);
    const distanceScore = Math.max(0, 1 - (distance - 50_000) / Math.max(1, maxDistance - 50_000));
    const rankScore = Math.max(0, Math.min(1, Number(rank || 0) / 1000));
    const confidence = Math.min(0.88, alignment * 0.62 + distanceScore * 0.22 + rankScore * 0.06 + 0.10);
    const visibilityState = Math.abs(relativeAngle) <= query.horizontalFov / 2 ? "VISIBLE" : "OUTSIDE_VIEW";
    const status: GeoArStatus[] = [visibilityState, ...warnings];
    if (query.offline) status.push("OFFLINE_AVAILABLE");

    candidates.push({
      score: confidence,
      result: {
        feature_uuid: `range-${String(id)}`,
        name: String(name),
        feature_type: featureType,
        distance,
        bearing,
        relative_angle: relativeAngle,
        elevation_angle: null,
        relative_vertical_angle: null,
        visibility_state: visibilityState,
        confidence,
        candidate_rank: null,
        alignment_warning: false,
        accuracy_warning: [...warnings],
        data_source: String(id).startsWith("map-")
          ? "THERA Range Catalog · PMTiles"
          : `THERA Range Catalog · ${payload.attribution || payload.source || "GeoNames"}`,
        feature_altitude_m: validAltitude,
        feature_altitude_source: validAltitude === null ? null : (typeof elevationSource === "string" && elevationSource.trim() ? elevationSource : null),
        feature_altitude_status: validAltitude === null ? null : (typeof elevationStatus === "string" && elevationStatus.trim() ? elevationStatus : null),
        offline: query.offline,
        status,
      },
    });
  }

  candidates.sort((left, right) => right.score - left.score
    || Math.abs(left.result.relative_angle ?? Infinity) - Math.abs(right.result.relative_angle ?? Infinity)
    || (left.result.distance ?? Infinity) - (right.result.distance ?? Infinity));

  return {
    results: candidates.slice(0, Math.max(1, Math.min(12, query.maxResults ?? 8))).map(({ result }, index) => ({ ...result, candidate_rank: index + 1 })),
    scanned: tuples.length,
    source: mapCatalog.tuples.length ? "THERA Range Catalog · PMTiles + Gazetteer" : "THERA Range Catalog · Gazetteer",
    mapCatalogReady: mapCatalog.ready,
    mapCatalogBuilding: mapCatalog.building,
  };
}
